import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, inject } from "@angular/core";

import { Medida, numero } from "../cc-formato";
import { MS_CIFRA, cuenta } from "./movimiento-cc";

/** El perímetro del arco (r = 15.5 en un lienzo de 36 × 36). */
const PERIMETRO = 2 * Math.PI * 15.5;

/** El color del arco: pasa (verde), aviso (amarillo), no pasa (rojo) o neutro. */
export type TonoAnilloCc = "pasa" | "aviso" | "no-pasa" | "neutro";

/** Desde qué cobertura el anillo va en verde: 80 % o más. */
export const COBERTURA_VERDE = 80;
/** Desde qué cobertura el anillo va en amarillo (y hasta antes de COBERTURA_VERDE); abajo de esto, en rojo. */
export const COBERTURA_AMARILLA = 50;

/**
 * El color del anillo de una cobertura según su porcentaje: verde con 80 % o más, amarillo de 50 % a menos de 80 %
 * y rojo abajo de 50 %; sin dato, neutro.
 */
export function tonoCobertura(valor: Medida): TonoAnilloCc {
  const n = numero(valor);
  if (n == null) {
    return "neutro";
  }
  if (n >= COBERTURA_VERDE) {
    return "pasa";
  }
  return n >= COBERTURA_AMARILLA ? "aviso" : "no-pasa";
}

/** Hasta qué duplicación el anillo va en verde: 3 % o menos. */
export const DUPLICACION_VERDE = 3;
/** Hasta qué duplicación el anillo va en amarillo (desde arriba de DUPLICACION_VERDE); arriba de esto, en rojo. */
export const DUPLICACION_AMARILLA = 10;

/**
 * El color del anillo de una duplicación según su porcentaje (menos es mejor): verde con 3 % o menos, amarillo de
 * más de 3 % a 10 % y rojo arriba de 10 %; sin dato, neutro.
 */
export function tonoDuplicacion(valor: Medida): TonoAnilloCc {
  const n = numero(valor);
  if (n == null) {
    return "neutro";
  }
  if (n <= DUPLICACION_VERDE) {
    return "pasa";
  }
  return n <= DUPLICACION_AMARILLA ? "aviso" : "no-pasa";
}

/**
 * Un anillo de progreso (0..100 %) que se llena al entrar (≤ 600 ms) con el
 * tono: pasa (verde), aviso (amarillo), no pasa (rojo) o neutro. El arco es un SVG cuyo
 * stroke-dashoffset se mueve cuadro a cuadro desde el componente (el mismo
 * reloj que las cifras); con prefers-reduced-motion queda lleno de inmediato.
 * Con pistaConTono (la cobertura y la duplicación), la pista también lleva el color del tono, tenue: así se ve el
 * color aunque el arco sea mínimo o no lo haya (0.0 % de cobertura, 0.3 % de duplicación).
 * La cifra va al lado, en texto (app-cifra-cc): el anillo solo la dibuja.
 */
@Component({
  selector: "app-anillo-cc",
  standalone: true,
  template: `
    <svg viewBox="0 0 36 36" aria-hidden="true" focusable="false">
      <circle class="cc-anillo__pista" cx="18" cy="18" r="15.5"></circle>
      <circle class="cc-anillo__arco" cx="18" cy="18" r="15.5" [attr.stroke-dasharray]="perimetro"
              [attr.stroke-dashoffset]="desfase"></circle>
    </svg>
  `,
  styles: `
    :host {
      /* Amarillo dorado en los temas claros: el ámbar del tema se ve naranja sobre blanco. */
      --cc-anillo-aviso: #ca8a04;
      align-items: center;
      display: inline-flex;
      flex: 0 0 auto;
      height: 30px;
      justify-content: center;
      width: 30px;
    }

    svg {
      height: 100%;
      transform: rotate(-90deg);
      width: 100%;
    }

    circle {
      fill: none;
      stroke-width: 4;
    }

    .cc-anillo__pista {
      stroke: var(--cc-anillo-pista, var(--cos-superficie-3, #e8edf2));
    }

    .cc-anillo__arco {
      stroke: var(--cc-anillo-color, var(--cos-info, #1d4ed8));
      stroke-linecap: round;
    }

    :host(.is-pasa) .cc-anillo__arco {
      stroke: var(--cos-exito, #16a34a);
    }

    /* El amarillo del oscuro: el del tema (en los claros, el dorado de :host). */
    :host-context([data-tema="oscuro"]) {
      --cc-anillo-aviso: var(--cos-aviso, #fbbf24);
    }

    :host(.is-aviso) .cc-anillo__arco {
      stroke: var(--cc-anillo-aviso);
    }

    :host(.is-no-pasa) .cc-anillo__arco {
      stroke: var(--cos-peligro, #b91c1c);
    }

    :host(.is-neutro) .cc-anillo__arco {
      stroke: var(--cc-anillo-neutro, var(--cos-texto-2, #475569));
    }

    :host(.con-pista.is-pasa) .cc-anillo__pista {
      stroke: color-mix(in srgb, var(--cos-exito, #16a34a) 32%, transparent);
    }

    :host(.con-pista.is-aviso) .cc-anillo__pista {
      stroke: color-mix(in srgb, var(--cc-anillo-aviso) 32%, transparent);
    }

    :host(.con-pista.is-no-pasa) .cc-anillo__pista {
      stroke: color-mix(in srgb, var(--cos-peligro, #b91c1c) 40%, transparent);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnilloCcComponent implements OnChanges, OnDestroy {
  /** 0..100 (texto crudo de SonarQube o número); nulo = anillo vacío. */
  @Input() valor: Medida = null;
  /** pasa | aviso | no-pasa | neutro: la clase is-* del host. */
  @Input() tono: TonoAnilloCc = "neutro";
  /** La pista también lleva el color del tono, tenue (cobertura y duplicación): la clase con-pista del host. */
  @Input() pistaConTono = false;

  readonly perimetro = PERIMETRO;
  desfase = PERIMETRO;

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private cancela: () => void = () => undefined;
  private actual = 0;

  ngOnChanges(): void {
    this.cancela();
    const clases = this.host.nativeElement.classList;
    clases.remove("is-pasa", "is-aviso", "is-no-pasa", "is-neutro");
    clases.add(`is-${this.tono}`);
    clases.toggle("con-pista", this.pistaConTono);
    const n = numero(this.valor);
    if (n == null) {
      this.actual = 0;
      this.pinta(0);
      return;
    }
    const meta = Math.min(100, Math.max(0, n));
    this.cancela = cuenta(this.actual, meta, MS_CIFRA, (v) => this.pinta(v));
    this.actual = meta;
  }

  ngOnDestroy(): void {
    this.cancela();
  }

  /** Mueve el arco sin pasar por la detección de cambios (un cuadro por llamada). */
  private pinta(v: number): void {
    this.desfase = PERIMETRO * (1 - v / 100);
    const arco = this.host.nativeElement.querySelector(".cc-anillo__arco");
    arco?.setAttribute("stroke-dashoffset", `${this.desfase}`);
  }
}
